/**
 * useLoyaltyRules — shared hook that fetches `/api/loyalty-rules/public`
 * ONCE per page load and caches the result in module scope so any surface
 * (Companion referral banner, Rewards screen, landing hero) can render
 * dynamic amounts that auto-update when a super admin edits the config
 * in Loyalty & Coupons → Loyalty Rules card.
 *
 * Fields exposed (matches loyalty_rules.py DEFAULTS):
 *   • referral_amount               ($ each side on first paid order)
 *   • referral_cap                  (max rewarded referrals per user)
 *   • companion_install_bonus_points  (pts referrer earns on friend signup)
 *   • one_time_amount, subscription_amount, royal_amount, …
 *
 * Usage:
 *   const rules = useLoyaltyRules();
 *   rules.companion_install_bonus_points  // e.g. 1000
 */
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Module-scope cache — a single in-flight promise is shared across components
// so we don't fan out N concurrent requests on a single page.
let _cachedRules = null;
let _inflight = null;

async function fetchRulesOnce() {
  if (_cachedRules) return _cachedRules;
  if (_inflight) return _inflight;
  _inflight = axios
    .get(`${BACKEND}/api/loyalty-rules/public`)
    .then((r) => { _cachedRules = r.data || {}; return _cachedRules; })
    .catch(() => ({}))
    .finally(() => { _inflight = null; });
  return _inflight;
}

// Fallback shape mirrors DEFAULTS in loyalty_rules.py — used before the
// first successful fetch so UIs never render "undefined pts".
const FALLBACK = {
  referral_amount: 20,
  referral_cap: 10,
  companion_install_bonus_points: 1000,
  one_time_amount: 10,
  subscription_amount: 15,
  royal_amount: 100,
  switch_save_amount: 20,
  new_patient_price: 150,
};

export default function useLoyaltyRules() {
  const [rules, setRules] = useState(_cachedRules || FALLBACK);
  useEffect(() => {
    let alive = true;
    fetchRulesOnce().then((r) => { if (alive) setRules({ ...FALLBACK, ...r }); });
    return () => { alive = false; };
  }, []);
  return rules;
}

// Escape hatch — allow the admin edit flow to invalidate the cache after
// a save so patient-side components refetch on their next mount.
export function invalidateLoyaltyRulesCache() {
  _cachedRules = null;
}
